import React, { useState } from "react"
import { Typography, Divider, Row, Col, Tooltip, Collapse, InputNumber, Button, Input } from "antd"
import PropTypes from "prop-types"
import { useWatch } from "antd/es/form/Form"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { numberToCurrency } from "helpers/string"
import { InfoCircleOutlined, EditOutlined, ReloadOutlined, CheckOutlined } from "@ant-design/icons"
import { calculatePaymentBreakdown } from "utils/bookingCalculations"
import { computeDiscountAmount } from "utils/discountUtils"
import { colors } from "themes/variables"

import { isFailedPaymentStatus } from "ui/Booking/Details/utils"
import FlatRatePriceTooltip from "ui/FlatRatePriceTooltip"
import styles from "./BookingForm.module.css"

const { Text } = Typography

// The card fee line plus the amount it makes up. Also under an order's total, which has no
// PriceBreakdown of its own.
export const CardFeeRows = ({ cardProcessingFee, chargedAmount }) => {
  const { t } = useTranslation()
  return (
    <>
      <Row justify="space-between" style={{ marginTop: 8 }}>
        <Col>
          <Text>{t("booking.labels.card_processing_fee")}</Text>
        </Col>
        <Col>
          <Text>+{numberToCurrency(cardProcessingFee)}</Text>
        </Col>
      </Row>
      <Row justify="space-between" style={{ marginTop: 8 }}>
        <Col>
          <Text strong>{t("booking.labels.total_charged_to_card", "Total Charged to Card")}</Text>
        </Col>
        <Col>
          <Text strong>{numberToCurrency(chargedAmount)}</Text>
        </Col>
      </Row>
    </>
  )
}

CardFeeRows.propTypes = {
  cardProcessingFee: PropTypes.number.isRequired,
  chargedAmount: PropTypes.number.isRequired
}

// Pure derivations pulled out of PriceBreakdown so their branching doesn't count toward its own
// cognitive complexity (mirrors the same pattern used in ExperienceDetail.jsx).
const resolveIsAdjusted = ({ isEdit, paymentSummary, adjustedPrice }) => {
  const bookingSubtotalAdjusted = !!(isEdit && paymentSummary.subtotal_adjusted && paymentSummary.adjusted_subtotal != null)
  return (adjustedPrice != null && adjustedPrice >= 0) || bookingSubtotalAdjusted
}

const resolveExistingDiscount = ({ paymentSummary, discountAmount, baseAmount, addOnsTotal }) => {
  if (!(paymentSummary?.discount_type && paymentSummary?.discount_value != null)) {
    return Math.max(0, Number(discountAmount || 0))
  }
  return computeDiscountAmount(
    {
      value: Number(paymentSummary.discount_value),
      discount_type: paymentSummary.discount_type,
      apply_to_add_ons: paymentSummary?.applicability_rules?.apply_to_add_ons ?? paymentSummary?.apply_to_add_ons ?? false
    },
    baseAmount,
    addOnsTotal
  )
}

const resolveEffectiveEditDiscount = ({ isEdit, isExperienceChanged, isOriginalDiscountRemoved, recomputedExistingDiscount, appliedPromos }) => {
  // Two things end the booking's saved discount: swapping the experience, and the guide removing
  // the coupon. Either way it arrives in appliedPromos if it survives, so falling back to
  // payment_summary would put the old code's row and its total back on screen.
  const noNewPromoApplied =
    isEdit && !isExperienceChanged && !isOriginalDiscountRemoved && recomputedExistingDiscount > 0 && appliedPromos.length === 0
  return noNewPromoApplied ? recomputedExistingDiscount : 0
}

// The "Subtotal" row plus its inline price-adjust panel is only ever used together and touches no
// state outside itself — pulled into its own component so its many independent conditionals (edit
// button, reset button, adjusted-vs-original display, reason line, edit panel) don't all count
// toward PriceBreakdown's own cognitive complexity.
const SubtotalSection = ({
  t,
  isEdit,
  guestLines,
  renderBreakdownItem,
  isAdjusted,
  adjustedPrice,
  originalBaseAmount,
  baseAmount,
  adjustmentReason,
  form,
  isChargesOnly
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [localValue, setLocalValue] = useState(null)
  const [localReason, setLocalReason] = useState("")

  if (guestLines.length === 0) return null

  const handleEditClick = () => {
    setLocalValue(isAdjusted ? adjustedPrice : originalBaseAmount)
    setLocalReason(adjustmentReason ?? "")
    setIsEditing(true)
  }

  const handleApply = () => {
    form.setFieldsValue({ adjustedPrice: localValue, adjustmentReason: localReason?.trim() || null })
    setIsEditing(false)
  }

  const handleCancel = () => setIsEditing(false)

  const handleReset = () => {
    form.setFieldsValue({ adjustedPrice: null, adjustmentReason: null })
    setLocalReason("")
    setIsEditing(false)
  }

  return (
    <>
      {!isChargesOnly && guestLines.map(renderBreakdownItem)}
      <Row justify="space-between" align="middle" style={{ marginTop: 8 }}>
        <Col>
          <Text style={{ fontWeight: 500 }}>{t("booking.labels.subtotal", "Subtotal")}</Text>
          {!isEdit && !isEditing && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={handleEditClick}
              style={{ padding: "0 4px", color: "var(--color-primary)", fontSize: 13 }}
            >
              {t("global.adjust", "Adjust")}
            </Button>
          )}
          {!isEdit && !isEditing && isAdjusted && (
            <Button
              type="link"
              size="small"
              icon={<ReloadOutlined />}
              onClick={handleReset}
              style={{ padding: "0 4px", color: "var(--color-text-secondary)", fontSize: 13 }}
            >
              {t("booking.labels.reset_adjustment")}
            </Button>
          )}
        </Col>
        <Col>
          {isAdjusted ? (
            <span>
              <Text delete type="secondary" style={{ marginRight: 6 }}>
                {numberToCurrency(originalBaseAmount)}
              </Text>
              <Text strong style={{ color: "var(--color-primary)", marginRight: 4 }}>
                {numberToCurrency(baseAmount)}
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                ({t("global.adjusted", "adjusted")})
              </Text>
            </span>
          ) : (
            <Text>{numberToCurrency(originalBaseAmount)}</Text>
          )}
        </Col>
      </Row>

      {/* Reason is shown in edit mode too (read-only) so the carried-over adjustment stays legible. */}
      {!isEditing && isAdjusted && adjustmentReason && (
        <Row style={{ marginTop: 2 }}>
          <Col>
            <Text type="secondary" style={{ fontSize: 12, fontStyle: "italic" }}>
              {t("booking.labels.reason", "Reason")}: {adjustmentReason}
            </Text>
          </Col>
        </Row>
      )}

      {!isEdit && isEditing && (
        <div className={styles.adjustPricePanel}>
          <Text className={styles.adjustPricePanelTitle}>{t("booking.adjusted_price_label", "Adjusted Price (Subtotal)")}</Text>
          <InputNumber
            value={localValue}
            onChange={setLocalValue}
            min={0}
            precision={2}
            prefix="$"
            size="large"
            style={{ width: "100%", marginTop: 8 }}
            autoFocus
            // Numeric-only: block letters/"e"/symbols on keypress and strip them on paste.
            onKeyDown={(e) => {
              const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End", "Enter"]
              if (allowedKeys.includes(e.key) || e.metaKey || e.ctrlKey) return
              if (!/^[0-9.]$/.test(e.key)) e.preventDefault()
            }}
            parser={(value) => (value ? value.replace(/[^\d.]/g, "") : "")}
          />
          <Input.TextArea
            value={localReason}
            onChange={(e) => setLocalReason(e.target.value)}
            placeholder={t("booking.adjustment_reason_placeholder", "Reason for adjustment (optional)")}
            autoSize={{ minRows: 2, maxRows: 4 }}
            maxLength={255}
            style={{ marginTop: 8 }}
          />
          <Text type="secondary" style={{ fontSize: 12, marginTop: 6, display: "block" }}>
            {t("booking.adjust_price_hint", "Fees are automatically recalculated based on adjusted price")}
          </Text>
          <Row justify="end" gutter={8} style={{ marginTop: 12 }}>
            <Col flex="none">
              <Button size="large" onClick={handleCancel}>
                {t("global.cancel")}
              </Button>
            </Col>
            <Col flex="none">
              <Button type="primary" icon={<CheckOutlined />} size="large" onClick={handleApply} disabled={localValue == null}>
                {t("global.apply", "Apply")}
              </Button>
            </Col>
          </Row>
        </div>
      )}
    </>
  )
}

SubtotalSection.propTypes = {
  t: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  guestLines: PropTypes.array.isRequired,
  renderBreakdownItem: PropTypes.func.isRequired,
  isAdjusted: PropTypes.bool,
  adjustedPrice: PropTypes.number,
  originalBaseAmount: PropTypes.number,
  baseAmount: PropTypes.number,
  adjustmentReason: PropTypes.string,
  form: PropTypes.object.isRequired,
  isChargesOnly: PropTypes.bool
}

const PaymentHistoryRow = ({ history, t }) => {
  const { type, method, last4, amount, id, status } = history || {}
  const isRefund = type?.toLowerCase() === "refund"
  // A failed row is money that never moved, and the breakdown below already ignores it. Without the
  // marker a declined refund reads as cash the guest got back, which makes the balance look wrong
  // rather than explaining itself.
  const isFailed = isFailedPaymentStatus(status)
  const key = id || `${type}-${method}-${last4}-${amount}`
  return (
    <Row key={key} justify="space-between" style={{ marginTop: 8 }}>
      <Col>
        <Text>
          {type || ""} ({method || ""}
          {last4 && ` **** ${last4}`})
          {isFailed && ` — ${t("booking.reservation_detail.payment_failed")}`}
        </Text>
      </Col>
      <Col>
        <Text delete={isFailed} color={isFailed || isRefund ? colors.error : colors.textPrimary}>
          {amount ? numberToCurrency(amount) : ""}
        </Text>
      </Col>
    </Row>
  )
}

PaymentHistoryRow.propTypes = {
  history: PropTypes.object,
  t: PropTypes.func.isRequired
}

// Deposit/remaining-balance/refund rows, pulled out so their several independent visibility
// conditions don't all count toward PriceBreakdown's own cognitive complexity.
const PaymentSummarySection = ({
  percent,
  isEdit,
  paymentHistory,
  showDepositDueNow,
  showRemainingBalance,
  depositOnly,
  t,
  depositDueNow,
  depositAmount,
  balanceAmount,
  refundDueNow,
  cardProcessingFee = 0,
  depositDueNowAmount = 0,
  renderCardFeeRows
}) => (
  <>
    {percent > 0 && (
      <>
        {isEdit && paymentHistory?.map((history) => <PaymentHistoryRow key={history?.id || history?.type} history={history} t={t} />)}
        {showDepositDueNow && (
          <>
            <Row justify="space-between" style={{ marginTop: 8 }}>
              <Col>
                <Text>{isEdit ? t("booking.labels.deposit_due_now") : depositOnly && t("booking.labels.pay_now_deposit")}</Text>
              </Col>
              <Col>
                {/* The deposit shown is the base: its card fee is added on the line below so the
                    fee never hides inside another amount. */}
                <Text>{numberToCurrency(Math.max(0, depositDueNowAmount - cardProcessingFee))}</Text>
              </Col>
            </Row>
            {cardProcessingFee > 0 && renderCardFeeRows && renderCardFeeRows(depositDueNowAmount)}
          </>
        )}
        {showRemainingBalance && (
          <Row justify="space-between" style={{ marginTop: 8 }}>
            <Col>
              <Text strong>{t("booking.labels.remaining_balance")}</Text>
            </Col>
            <Col>
              <Text strong>{numberToCurrency(balanceAmount)}</Text>
            </Col>
          </Row>
        )}
      </>
    )}
    {isEdit && refundDueNow > 0 && (
      <Row justify="space-between" style={{ marginTop: 8 }}>
        <Col>
          <Text strong>{t("booking.labels.refund_due")}</Text>
        </Col>
        <Col>
          <Text strong>{numberToCurrency(refundDueNow)}</Text>
        </Col>
      </Row>
    )}
  </>
)

PaymentSummarySection.propTypes = {
  percent: PropTypes.number,
  isEdit: PropTypes.bool,
  paymentHistory: PropTypes.array,
  showDepositDueNow: PropTypes.bool,
  showRemainingBalance: PropTypes.bool,
  depositOnly: PropTypes.bool,
  t: PropTypes.func.isRequired,
  depositDueNow: PropTypes.number,
  depositAmount: PropTypes.number,
  balanceAmount: PropTypes.number,
  refundDueNow: PropTypes.number,
  cardProcessingFee: PropTypes.number,
  depositDueNowAmount: PropTypes.number,
  renderCardFeeRows: PropTypes.func
}

const PriceBreakdown = ({ form, booking, isEdit, isPriceDecreased, hasDepositPayment, appliedPromos = [], isExperienceChanged = false, isOriginalDiscountRemoved = false, isChargesOnly = false, bookingSelections }) => {
  const paymentSummary = booking?.payment_summary ?? {}
  const { discount_amount, discount_code } = paymentSummary
  const { t } = useTranslation()
  const {
    experienceById: { experienceDetail }
  } = useSelector((state) => state.experiences)
  // The newly-selected experience's own deposit_percentage only governs when the experience was
  // actually swapped during this edit — the booking's stored deposit_percentage is a snapshot of
  // whichever experience it was ORIGINALLY booked against and must remain authoritative otherwise
  // (mirrors the same isExperienceChanged gating in EditBookingForm.jsx/PaymentMethod.jsx).
  const depositPercent =
    useWatch("depositPercent", { form, preserve: true }) ||
    (isExperienceChanged ? experienceDetail?.deposit_percentage : booking?.deposit_percentage) ||
    ""
  const customDepositAmount = useWatch("customDepositAmount", { form, preserve: true })
  const paymentOption = useWatch("paymentOption", { form, preserve: true })
  const depositOnly = useWatch("depositOnly", form)
  const adjustedPrice = useWatch("adjustedPrice", form)
  const adjustmentReasonWatch = useWatch("adjustmentReason", form)
  // In edit mode the adjustment is read from the booking; fall back to it so the struck-through
  // adjusted display and reason render even before the watched form fields settle.
  const isAdjusted = resolveIsAdjusted({ isEdit, paymentSummary, adjustedPrice })
  const adjustmentReason = adjustmentReasonWatch ?? (isEdit ? paymentSummary.subtotal_adjustment_reason : null)

  const baseAmount = bookingSelections?.baseAmount ?? 0
  const addOnsTotal = bookingSelections?.addOnsTotal ?? 0
  const originalBaseAmount = bookingSelections?.originalBaseAmount ?? bookingSelections?.baseAmount ?? 0

  const recomputedExistingDiscount = resolveExistingDiscount({ paymentSummary, discountAmount: discount_amount, baseAmount, addOnsTotal })
  // The saved booking's discount is shown until the user changes the promo — but not once the
  // experience has been swapped, since the old coupon no longer applies to the new experience.
  const effectiveEditDiscount = resolveEffectiveEditDiscount({ isEdit, isExperienceChanged, isOriginalDiscountRemoved, recomputedExistingDiscount, appliedPromos })

  const {
    percent,
    depositAmount,
    depositDueNow,
    balanceAmount,
    payment_history = [],
    refundDueNow
  } = calculatePaymentBreakdown({
    booking,
    bookingSelections,
    depositPercent,
    customDepositAmount,
    isPriceDecreased,
    paymentOption,
    isDepositOverride: isEdit ? hasDepositPayment : undefined
  })

  const breakdown = bookingSelections?.breakdown ?? []
  // Post-creation price-adjustment lines render after Tax & Fees / discounts and before the total,
  // so pull them out first to keep their placement independent of whether a Tax & Fees line exists.
  const priceChangeLines = breakdown.filter((item) => item?.isPriceChange)
  const nonPriceChange = breakdown.filter((item) => !item?.isPriceChange)
  const taxIndex = nonPriceChange.findIndex(
    (item) => item?.isTaxFee || (typeof item?.label === "string" && item.label.toLowerCase().includes("tax"))
  )
  const beforeTax = taxIndex >= 0 ? nonPriceChange.slice(0, taxIndex) : nonPriceChange
  const taxAndAfter = taxIndex >= 0 ? nonPriceChange.slice(taxIndex) : []

  // Guest lines are everything before "Adjusted Price" in the breakdown
  const guestLines = beforeTax.filter((item) => item?.label !== "Adjusted Price")

  // Reused for both the generic `info` string and the clamped-to-$0 explanation, so a line can
  // carry either without the caller having to know the copy.
  const renderInfoIcon = (title) => (
    <Tooltip title={title}>
      <InfoCircleOutlined className="ml-xxs" style={{ color: "var(--color-primary)" }} />
    </Tooltip>
  )

  const renderBreakdownItem = (item) =>
    item?.children?.length > 0 ? (
      <Collapse
        key={`${item?.label}-${item?.amount}`}
        ghost
        style={{ marginTop: 8 }}
        items={[
          {
            key: "tax-fees",
            styles: {
              header: { padding: 0 },
              body: { padding: "4px 0 0 16px" }
            },
            label: (
              <Row justify="space-between">
                <Col>
                  <Text>{item?.label}</Text>
                </Col>
                <Col>
                  <Text>{item?.amount}</Text>
                </Col>
              </Row>
            ),
            children: item.children.map((child) => (
              <Row key={`${child?.label}-${child?.amount}`} justify="space-between" style={{ marginTop: 4 }}>
                <Col>
                  <Text type="secondary">{child?.label}</Text>
                </Col>
                <Col>
                  <Text type="secondary">{child?.amount}</Text>
                </Col>
              </Row>
            ))
          }
        ]}
      />
    ) : (
      <Row key={`${item?.label}-${item?.amount}`} justify="space-between" style={{ marginTop: 8 }}>
        <Col>
          <Text>{item?.label}</Text>
          {item?.pricingDetails && <FlatRatePriceTooltip {...item.pricingDetails} />}
          {item?.isZeroedByAdjustment
            ? renderInfoIcon(t("booking.warnings.price_zeroed_by_adjustment"))
            : !item?.pricingDetails && item?.info && renderInfoIcon(item.info)}
        </Col>
        <Col>
          <Text>{item?.amount}</Text>
        </Col>
      </Row>
    )

  const totalAfterDiscounts = Math.max(0, bookingSelections?.amount ?? 0)
  // The card processing fee is a transaction fee on the amount being charged, not part of the
  // booking price: the Total is struck first, then the fee is added below it. `preCardTotal` is
  // that total; `amount` is what actually hits the card.
  const cardProcessingFee = Math.max(0, bookingSelections?.cardProcessingFee ?? 0)
  const preCardTotal = cardProcessingFee > 0 ? Math.max(0, bookingSelections?.preCardTotal ?? 0) : totalAfterDiscounts
  // What the card is actually charged now on a deposit booking — the outstanding deposit in edit
  // mode, the full deposit when creating. Both already include the card fee.
  const depositDueNowAmount = isEdit ? depositDueNow : depositAmount
  const hasAnyDiscount = appliedPromos.length > 0 || effectiveEditDiscount > 0
  const showDepositDueNow = (isEdit && hasDepositPayment && !isPriceDecreased) || (!isEdit && depositOnly)
  const showRemainingBalance =
    (isEdit && hasDepositPayment && balanceAmount > 0) ||
    (!isEdit && depositOnly && balanceAmount > 0) ||
    (isEdit && !hasDepositPayment && refundDueNow <= 0)

  // Shared by the pay-in-full and deposit paths.
  const renderCardFeeRows = (chargedAmount) => <CardFeeRows cardProcessingFee={cardProcessingFee} chargedAmount={chargedAmount} />

  return (
    <div>
      <SubtotalSection
        t={t}
        isEdit={isEdit}
        guestLines={guestLines}
        renderBreakdownItem={renderBreakdownItem}
        isAdjusted={isAdjusted}
        adjustedPrice={adjustedPrice}
        originalBaseAmount={originalBaseAmount}
        baseAmount={baseAmount}
        adjustmentReason={adjustmentReason}
        form={form}
        isChargesOnly={isChargesOnly}
      />

      {!isChargesOnly && addOnsTotal > 0 && (
        <Row justify="space-between" className={styles.priceRow}>
          <Col>
            <Text>{t("booking.add_ons.title")}</Text>
          </Col>
          <Col>
            <Text>{numberToCurrency(addOnsTotal)}</Text>
          </Col>
        </Row>
      )}

      {/* Discounts are applied to the pre-tax base, so they render before Tax & Fees. */}
      {!isChargesOnly && effectiveEditDiscount > 0 && (
        <Row justify="space-between" style={{ marginTop: 8 }}>
          <Col>
            <Text type="danger">{discount_code ? `Discount (${discount_code})` : "Discount"}</Text>
          </Col>
          <Col>
            <Text type="danger">-{numberToCurrency(effectiveEditDiscount)}</Text>
          </Col>
        </Row>
      )}

      {!isChargesOnly && appliedPromos.map((promo) => (
        <Row key={promo.code} justify="space-between" className={styles.priceRow}>
          <Col>
            <Text type="secondary">{promo.type === "voucher" ? t("booking.gift_card_with_code", { code: promo.code }) : `${promo.name} (${promo.code})`}</Text>
          </Col>
          <Col>
            <Text style={{ color: "var(--color-primary)" }}>
              -{numberToCurrency(computeDiscountAmount(promo, baseAmount, addOnsTotal))}
            </Text>
          </Col>
        </Row>
      ))}

      {/* isChargesOnly: New Booking lists the price lines and the Total in its Order Summary, so only
          the subtotal adjustment and what is charged now (card fee, deposit) are left here. */}
      {!isChargesOnly && (
        <>
          {taxAndAfter.map(renderBreakdownItem)}

          {priceChangeLines.map(renderBreakdownItem)}

          <Divider style={{ margin: "16px 0" }} />

          <Row justify="space-between" style={{ marginTop: hasAnyDiscount ? 8 : 0 }}>
            <Col>
              <Text strong>{hasAnyDiscount ? t("booking.labels.total_after_savings", { total: t("booking.labels.total") }) : t("booking.labels.total")}</Text>
            </Col>
            <Col>
              <Text strong>{numberToCurrency(preCardTotal)}</Text>
            </Col>
          </Row>
        </>
      )}

      {/* Paying in full: the fee is charged on the whole total, so it hangs off the Total directly.
          Deposit bookings charge it on the deposit instead — it is rendered below that row. */}
      {cardProcessingFee > 0 && !showDepositDueNow && renderCardFeeRows(totalAfterDiscounts)}
      <PaymentSummarySection
        percent={percent}
        isEdit={isEdit}
        paymentHistory={payment_history}
        showDepositDueNow={showDepositDueNow}
        showRemainingBalance={showRemainingBalance}
        depositOnly={depositOnly}
        t={t}
        depositDueNow={depositDueNow}
        depositAmount={depositAmount}
        balanceAmount={balanceAmount}
        refundDueNow={refundDueNow}
        cardProcessingFee={cardProcessingFee}
        depositDueNowAmount={depositDueNowAmount}
        renderCardFeeRows={renderCardFeeRows}
      />
    </div>
  )
}

PriceBreakdown.propTypes = {
  form: PropTypes.object.isRequired,
  booking: PropTypes.object.isRequired,
  isEdit: PropTypes.bool.isRequired,
  isPriceDecreased: PropTypes.bool.isRequired,
  hasDepositPayment: PropTypes.bool.isRequired,
  appliedPromos: PropTypes.array,
  bookingSelections: PropTypes.object,
  isExperienceChanged: PropTypes.bool,
  isOriginalDiscountRemoved: PropTypes.bool,
  isChargesOnly: PropTypes.bool
}

export default PriceBreakdown
